import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Users, DollarSign, Eye, Trash2, ArrowLeft, Shield, Plus, Clock, UserCog, Search, Tag, MessageSquare, Send, ExternalLink, BookOpen, BarChart3, History, TrendingUp, Percent } from "lucide-react";
import SeoToolkit from "@/components/SeoToolkit";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPanel({ token, onBack }) {
  const [dashboard, setDashboard] = useState(null);
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  // Create user dialog
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUser, setNewUser] = useState({ email: "", password: "", name: "", role: "user", plan: "free", trial_days: 0 });
  const [creating, setCreating] = useState(false);

  // Trial dialog
  const [showTrialDialog, setShowTrialDialog] = useState(false);
  const [trialTarget, setTrialTarget] = useState(null);
  const [trialDays, setTrialDays] = useState("1");

  // Pricing state
  const [pricingPlans, setPricingPlans] = useState([]);
  const [editingPlan, setEditingPlan] = useState(null);
  const [showCreatePlan, setShowCreatePlan] = useState(false);
  const [newPlan, setNewPlan] = useState({ plan_id: "", name: "", price: 0, duration_days: 0, description: "", active: true, sort_order: 99 });
  const [creatingPlan, setCreatingPlan] = useState(false);

  // Ticket state
  const [tickets, setTickets] = useState([]);
  const [ticketFilter, setTicketFilter] = useState("all");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyMessage, setReplyMessage] = useState("");
  const [replying, setReplying] = useState(false);

  // Analytics state
  const [analytics, setAnalytics] = useState(null);

  // Deletion history state
  const [deletionHistory, setDeletionHistory] = useState([]);
  const [deletionHistoryTotal, setDeletionHistoryTotal] = useState(0);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { loadDashboard(); }, []);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const [dashRes, usersRes, paymentsRes, pricingRes, ticketsRes, analyticsRes, historyRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard`, { headers }),
        axios.get(`${API}/admin/users?limit=200`, { headers }),
        axios.get(`${API}/admin/payments?limit=100`, { headers }),
        axios.get(`${API}/admin/pricing`, { headers }),
        axios.get(`${API}/admin/tickets`, { headers }),
        axios.get(`${API}/admin/analytics`, { headers }),
        axios.get(`${API}/admin/deletion-history?limit=100`, { headers }),
      ]);
      setDashboard(dashRes.data);
      setUsers(usersRes.data.users);
      setPayments(paymentsRes.data.payments);
      setPricingPlans(pricingRes.data.plans);
      setTickets(ticketsRes.data.tickets);
      setAnalytics(analyticsRes.data);
      setDeletionHistory(historyRes.data.records);
      setDeletionHistoryTotal(historyRes.data.total);
    } catch { toast.error("Failed to load admin data"); }
    finally { setLoading(false); }
  };

  const createUser = async () => {
    if (!newUser.email || !newUser.password) { toast.error("Email and password required"); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/admin/users/create`, newUser, { headers });
      toast.success(`User '${newUser.email}' created`);
      setShowCreateUser(false);
      setNewUser({ email: "", password: "", name: "", role: "user", plan: "free", trial_days: 0 });
      loadDashboard();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create user"); }
    finally { setCreating(false); }
  };

  const toggleAdmin = async (userId, currentAdmin) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, { is_admin: !currentAdmin }, { headers });
      toast.success(currentAdmin ? "Admin removed" : "Admin granted");
      setUsers(users.map(u => u.id === userId ? { ...u, is_admin: !currentAdmin } : u));
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to update"); }
  };

  const assignTrial = async () => {
    if (!trialTarget) return;
    try {
      await axios.post(`${API}/admin/users/${trialTarget.id}/trial`, { days: parseInt(trialDays) }, { headers });
      toast.success(`${trialDays}-day trial assigned to ${trialTarget.email}`);
      setShowTrialDialog(false);
      setUsers(users.map(u => u.id === trialTarget.id ? { ...u, plan: "trial", plan_expires: new Date(Date.now() + parseInt(trialDays) * 86400000).toISOString() } : u));
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to assign trial"); }
  };

  const deleteUser = async (userId, email) => {
    if (!window.confirm(`Delete user '${email}'? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers });
      toast.success(`User '${email}' deleted`);
      setUsers(users.filter(u => u.id !== userId));
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to delete user"); }
  };

  const changePlan = async (userId, plan) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, { plan }, { headers });
      toast.success(`Plan changed to ${plan}`);
      setUsers(users.map(u => u.id === userId ? { ...u, plan } : u));
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to change plan"); }
  };

  const filteredUsers = users.filter(u =>
    u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const updatePlan = async (planId, updates) => {
    try {
      await axios.put(`${API}/admin/pricing/${planId}`, updates, { headers });
      toast.success(`Plan '${planId}' updated`);
      setPricingPlans(pricingPlans.map(p => p.plan_id === planId ? { ...p, ...updates } : p));
      setEditingPlan(null);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to update plan"); }
  };

  const resetPricing = async () => {
    if (!window.confirm("Reset all plans to default prices?")) return;
    try {
      const res = await axios.post(`${API}/admin/pricing/reset`, {}, { headers });
      setPricingPlans(res.data.plans);
      toast.success("Pricing reset to defaults");
    } catch { toast.error("Failed to reset pricing"); }
  };

  const createPlan = async () => {
    if (!newPlan.plan_id || !newPlan.name || newPlan.price < 0) {
      toast.error("Plan ID, name, and valid price are required");
      return;
    }
    setCreatingPlan(true);
    try {
      const res = await axios.post(`${API}/admin/pricing`, newPlan, { headers });
      toast.success(`Plan '${newPlan.plan_id}' created`);
      setPricingPlans([...pricingPlans, res.data.plan]);
      setShowCreatePlan(false);
      setNewPlan({ plan_id: "", name: "", price: 0, duration_days: 0, description: "", active: true, sort_order: 99 });
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create plan"); }
    finally { setCreatingPlan(false); }
  };

  const deletePlan = async (planId) => {
    if (!window.confirm(`Delete plan '${planId}'? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/pricing/${planId}`, { headers });
      toast.success(`Plan '${planId}' deleted`);
      setPricingPlans(pricingPlans.filter(p => p.plan_id !== planId));
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to delete plan"); }
  };

  const replyToTicket = async (ticketId) => {
    if (!replyMessage.trim()) { toast.error("Reply message is required"); return; }
    setReplying(true);
    try {
      await axios.post(`${API}/admin/tickets/${ticketId}/reply`, { message: replyMessage }, { headers });
      toast.success("Reply sent");
      setReplyMessage("");
      // Refresh ticket
      const res = await axios.get(`${API}/admin/tickets`, { headers });
      setTickets(res.data.tickets);
      setSelectedTicket(res.data.tickets.find(t => t.ticket_id === ticketId) || null);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to send reply"); }
    finally { setReplying(false); }
  };

  const updateTicketStatus = async (ticketId, status) => {
    try {
      await axios.put(`${API}/admin/tickets/${ticketId}/status`, { status }, { headers });
      toast.success(`Ticket marked as '${status}'`);
      const res = await axios.get(`${API}/admin/tickets`, { headers });
      setTickets(res.data.tickets);
      if (selectedTicket?.ticket_id === ticketId) {
        setSelectedTicket(res.data.tickets.find(t => t.ticket_id === ticketId) || null);
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to update ticket"); }
  };

  if (loading) {
    return <div className="admin-loading" data-testid="admin-loading"><div className="admin-spinner" /><p>Loading admin panel...</p></div>;
  }

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "analytics", label: "Analytics" },
    { id: "users", label: "Users" },
    { id: "tickets", label: "Tickets" },
    { id: "pricing", label: "Pricing" },
    { id: "payments", label: "Payments" },
    { id: "history", label: "Deletion Log" },
    { id: "blog", label: "Blog" },
    { id: "seo", label: "SEO" },
  ];

  const getPlanExpiry = (u) => {
    if (!u.plan_expires) return null;
    const exp = new Date(u.plan_expires);
    const now = new Date();
    if (exp < now) return "Expired";
    const daysLeft = Math.ceil((exp - now) / 86400000);
    return `${daysLeft}d left`;
  };

  return (
    <div className="admin-panel" data-testid="admin-panel">
      <div className="admin-header">
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Button data-testid="admin-back-btn" variant="ghost" size="sm" onClick={onBack} style={{ color: "#fff" }}>
            <ArrowLeft className="w-4 h-4" style={{ marginRight: 6 }} />Back
          </Button>
          <Shield className="w-5 h-5" style={{ color: "#FFC220" }} />
          <h1>Market Mop Admin</h1>
        </div>
      </div>

      <div className="admin-tabs">
        {tabs.map(tab => (
          <button key={tab.id} data-testid={`admin-tab-${tab.id}`} className={`admin-tab-btn ${activeTab === tab.id ? "active" : ""}`} onClick={() => setActiveTab(tab.id)}>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "overview" && dashboard && (
        <div className="admin-content" data-testid="admin-overview">
          <div className="admin-stats-grid">
            <Card className="admin-stat-card">
              <CardHeader style={{ padding: "16px 16px 4px" }}><Users className="w-5 h-5" style={{ color: "var(--wm-blue)" }} /><CardTitle style={{ fontSize: "0.75rem", color: "var(--wm-text-secondary)", marginLeft: 8 }}>Total Users</CardTitle></CardHeader>
              <CardContent><div className="admin-stat-value">{dashboard.users.total}</div><div style={{ fontSize: "0.75rem", color: "var(--wm-text-muted)" }}>{dashboard.users.google} Google / {dashboard.users.email} Email</div></CardContent>
            </Card>
            <Card className="admin-stat-card">
              <CardHeader style={{ padding: "16px 16px 4px" }}><DollarSign className="w-5 h-5" style={{ color: "var(--wm-success)" }} /><CardTitle style={{ fontSize: "0.75rem", color: "var(--wm-text-secondary)", marginLeft: 8 }}>Revenue</CardTitle></CardHeader>
              <CardContent><div className="admin-stat-value">${dashboard.payments.total_revenue.toFixed(2)}</div><div style={{ fontSize: "0.75rem", color: "var(--wm-text-muted)" }}>${dashboard.payments.monthly_revenue.toFixed(2)} Monthly / ${dashboard.payments.lifetime_revenue.toFixed(2)} Lifetime</div></CardContent>
            </Card>
            <Card className="admin-stat-card">
              <CardHeader style={{ padding: "16px 16px 4px" }}><Eye className="w-5 h-5" style={{ color: "#7C3AED" }} /><CardTitle style={{ fontSize: "0.75rem", color: "var(--wm-text-secondary)", marginLeft: 8 }}>Visits (7d)</CardTitle></CardHeader>
              <CardContent><div className="admin-stat-value">{dashboard.visits.last_7_days}</div><div style={{ fontSize: "0.75rem", color: "var(--wm-text-muted)" }}>{dashboard.visits.total} all time</div></CardContent>
            </Card>
            <Card className="admin-stat-card">
              <CardHeader style={{ padding: "16px 16px 4px" }}><Trash2 className="w-5 h-5" style={{ color: "var(--wm-danger)" }} /><CardTitle style={{ fontSize: "0.75rem", color: "var(--wm-text-secondary)", marginLeft: 8 }}>SKUs Retired</CardTitle></CardHeader>
              <CardContent><div className="admin-stat-value">{dashboard.skus_retired}</div></CardContent>
            </Card>
          </div>

          <div className="admin-stats-grid" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
            <Card className="admin-stat-card"><CardContent style={{ padding: 20 }}><div className="admin-stat-value" style={{ color: "var(--wm-text-muted)" }}>{dashboard.users.free}</div><div className="admin-stat-label">Free Users</div></CardContent></Card>
            <Card className="admin-stat-card"><CardContent style={{ padding: 20 }}><div className="admin-stat-value" style={{ color: "var(--wm-blue)" }}>{dashboard.users.monthly}</div><div className="admin-stat-label">Monthly Subs</div></CardContent></Card>
            <Card className="admin-stat-card"><CardContent style={{ padding: 20 }}><div className="admin-stat-value" style={{ color: "var(--wm-orange, #F08C00)" }}>{dashboard.users.lifetime}</div><div className="admin-stat-label">Lifetime Users</div></CardContent></Card>
          </div>
        </div>
      )}

      {activeTab === "analytics" && analytics && (
        <div className="admin-content" data-testid="admin-analytics-tab">
          <h2 style={{ fontSize: "1.25rem", fontWeight: 700, color: "var(--wm-text)", margin: "0 0 20px" }}>Analytics Dashboard</h2>

          {/* KPI Cards */}
          <div className="admin-stats-grid" style={{ gridTemplateColumns: "repeat(4, 1fr)", marginBottom: 24 }}>
            <Card className="admin-stat-card">
              <CardContent style={{ padding: 20, textAlign: "center" }}>
                <TrendingUp className="w-5 h-5" style={{ color: "var(--wm-blue)", margin: "0 auto 8px" }} />
                <div className="admin-stat-value">${analytics.recent_30d.revenue.toFixed(2)}</div>
                <div className="admin-stat-label">30-Day Revenue</div>
              </CardContent>
            </Card>
            <Card className="admin-stat-card">
              <CardContent style={{ padding: 20, textAlign: "center" }}>
                <Percent className="w-5 h-5" style={{ color: "var(--wm-success)", margin: "0 auto 8px" }} />
                <div className="admin-stat-value">{analytics.conversion_rate}%</div>
                <div className="admin-stat-label">Conversion Rate</div>
              </CardContent>
            </Card>
            <Card className="admin-stat-card">
              <CardContent style={{ padding: 20, textAlign: "center" }}>
                <MessageSquare className="w-5 h-5" style={{ color: "#F08C00", margin: "0 auto 8px" }} />
                <div className="admin-stat-value">{analytics.tickets.open}</div>
                <div className="admin-stat-label">Open Tickets</div>
              </CardContent>
            </Card>
            <Card className="admin-stat-card">
              <CardContent style={{ padding: 20, textAlign: "center" }}>
                <DollarSign className="w-5 h-5" style={{ color: "#7C3AED", margin: "0 auto 8px" }} />
                <div className="admin-stat-value">{analytics.recent_30d.payments}</div>
                <div className="admin-stat-label">30-Day Payments</div>
              </CardContent>
            </Card>
          </div>

          {/* Plan Distribution */}
          <Card className="admin-stat-card" style={{ marginBottom: 24 }}>
            <CardHeader style={{ padding: "20px 24px 12px" }}>
              <CardTitle style={{ fontSize: "0.9375rem", fontWeight: 700, color: "var(--wm-text)" }}>Plan Distribution</CardTitle>
            </CardHeader>
            <CardContent style={{ padding: "0 24px 24px" }}>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                {Object.entries(analytics.plan_distribution).map(([plan, count]) => {
                  const total = Object.values(analytics.plan_distribution).reduce((a, b) => a + b, 0);
                  const pct = total > 0 ? (count / total * 100).toFixed(1) : 0;
                  const colors = { free: "#9CA3AF", daily: "#60A5FA", weekly: "#34D399", monthly: "#0071DC", yearly: "#8B5CF6", lifetime: "#F59E0B" };
                  return (
                    <div key={plan} style={{ flex: "1 1 120px", minWidth: 100 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                        <span style={{ fontSize: "0.75rem", fontWeight: 600, textTransform: "capitalize", color: "var(--wm-text)" }}>{plan}</span>
                        <span style={{ fontSize: "0.75rem", color: "var(--wm-text-muted)" }}>{count}</span>
                      </div>
                      <div style={{ height: 8, background: "#F3F4F6", borderRadius: 4, overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${pct}%`, background: colors[plan] || "#9CA3AF", borderRadius: 4, transition: "width 0.3s" }} />
                      </div>
                      <div style={{ fontSize: "0.625rem", color: "var(--wm-text-muted)", marginTop: 2 }}>{pct}%</div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Charts: Signups + Visits + Deletions (last 30 days as mini bar charts) */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
            {[
              { title: "Signups (30d)", data: analytics.daily_signups, color: "var(--wm-blue)" },
              { title: "Visits (30d)", data: analytics.daily_visits, color: "#7C3AED" },
              { title: "Deletions (30d)", data: analytics.daily_deletions, color: "var(--wm-danger)" },
            ].map(chart => {
              const max = Math.max(...chart.data.map(d => d.count), 1);
              const total = chart.data.reduce((a, d) => a + d.count, 0);
              return (
                <Card key={chart.title} className="admin-stat-card">
                  <CardHeader style={{ padding: "16px 20px 8px" }}>
                    <CardTitle style={{ fontSize: "0.8125rem", fontWeight: 700, color: "var(--wm-text)" }}>{chart.title}</CardTitle>
                  </CardHeader>
                  <CardContent style={{ padding: "0 20px 16px" }}>
                    <div style={{ fontSize: "1.5rem", fontWeight: 700, color: chart.color, marginBottom: 8 }}>{total}</div>
                    <div style={{ display: "flex", alignItems: "flex-end", gap: 1, height: 60 }}>
                      {chart.data.map((d, i) => (
                        <div key={i} title={`${d.date}: ${d.count}`} style={{
                          flex: 1, minWidth: 3, background: chart.color, opacity: 0.7,
                          height: `${Math.max((d.count / max) * 100, 3)}%`, borderRadius: "2px 2px 0 0",
                          transition: "height 0.2s",
                        }} />
                      ))}
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.5625rem", color: "var(--wm-text-muted)", marginTop: 4 }}>
                      <span>{chart.data[0]?.date?.slice(5)}</span>
                      <span>{chart.data[chart.data.length-1]?.date?.slice(5)}</span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {activeTab === "history" && (
        <div className="admin-content" data-testid="admin-history-tab">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <div>
              <h2 style={{ fontSize: "1.25rem", fontWeight: 700, color: "var(--wm-text)", margin: 0 }}>Deletion Audit Log</h2>
              <p style={{ fontSize: "0.8125rem", color: "var(--wm-text-muted)", margin: "4px 0 0" }}>
                {deletionHistoryTotal} total retired items
              </p>
            </div>
          </div>

          {deletionHistory.length === 0 ? (
            <div style={{ textAlign: "center", padding: 60, color: "var(--wm-text-muted)" }}>
              <History className="w-8 h-8" style={{ margin: "0 auto 12px", opacity: 0.4 }} />
              <p>No deletion history yet.</p>
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="data-table" data-testid="deletion-history-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>SKU</th>
                    <th>Product Name</th>
                    <th>User</th>
                  </tr>
                </thead>
                <tbody>
                  {deletionHistory.map((r, i) => (
                    <tr key={i} data-testid={`history-row-${i}`}>
                      <td style={{ fontSize: "0.8125rem", whiteSpace: "nowrap" }}>{r.retired_at ? new Date(r.retired_at).toLocaleString() : "-"}</td>
                      <td className="font-mono" style={{ fontSize: "0.8125rem" }}>{r.sku}</td>
                      <td style={{ fontSize: "0.8125rem", maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.product_name || "-"}</td>
                      <td style={{ fontSize: "0.8125rem" }}>{r.user_email || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === "users" && (
        <div className="admin-content" data-testid="admin-users-tab">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, maxWidth: 400 }}>
              <Search className="w-4 h-4" style={{ color: "var(--wm-text-muted)" }} />
              <Input data-testid="admin-user-search" placeholder="Search users..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} style={{ background: "var(--wm-white)", border: "1px solid var(--wm-border)", color: "var(--wm-text)" }} />
            </div>
            <Button data-testid="admin-create-user-btn" onClick={() => setShowCreateUser(true)} className="action-btn primary-btn">
              <Plus className="w-4 h-4" /> Create User
            </Button>
          </div>

          <Card className="admin-table-card">
            <CardHeader><CardTitle>Users ({filteredUsers.length})</CardTitle></CardHeader>
            <div style={{ padding: "0 24px 24px" }}>
              <div className="admin-table-container" style={{ maxHeight: 600, overflowY: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead style={{ position: "sticky", top: 0, zIndex: 2 }}>
                    <tr>
                      <th style={thStyle}>Email</th>
                      <th style={thStyle}>Name</th>
                      <th style={thStyle}>Plan</th>
                      <th style={thStyle}>Expires</th>
                      <th style={thStyle}>Provider</th>
                      <th style={thStyle}>Items</th>
                      <th style={thStyle}>Role</th>
                      <th style={thStyle}>Joined</th>
                      <th style={{ ...thStyle, textAlign: "center" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map(u => (
                      <tr key={u.id} style={{ borderBottom: "1px solid #f0f2f5" }}>
                        <td style={tdStyle}><span style={{ fontFamily: "monospace", fontSize: "0.8125rem" }}>{u.email}</span></td>
                        <td style={tdStyle}>{u.name || "-"}</td>
                        <td style={tdStyle}>
                          <Select value={u.plan || "free"} onValueChange={v => changePlan(u.id, v)}>
                            <SelectTrigger style={{ width: 110, height: 30, fontSize: "0.75rem" }} data-testid={`user-plan-select-${u.id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="free">Free</SelectItem>
                              <SelectItem value="trial">Trial</SelectItem>
                              <SelectItem value="monthly">Monthly</SelectItem>
                              <SelectItem value="lifetime">Lifetime</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td style={tdStyle}><span style={{ fontSize: "0.75rem", color: getPlanExpiry(u) === "Expired" ? "var(--wm-danger)" : "var(--wm-text-muted)" }}>{getPlanExpiry(u) || "-"}</span></td>
                        <td style={tdStyle}><span style={{ fontSize: "0.75rem" }}>{u.auth_provider || "email"}</span></td>
                        <td style={tdStyle}>{u.items_deleted || 0}</td>
                        <td style={tdStyle}>
                          {u.is_admin && <span className="plan-badge monthly" style={{ marginRight: 4 }}>Admin</span>}
                          {u.is_test && <span className="plan-badge" style={{ background: "#FEF3C7", color: "#92400E" }}>Test</span>}
                          {!u.is_admin && !u.is_test && <span style={{ color: "var(--wm-text-muted)", fontSize: "0.75rem" }}>User</span>}
                        </td>
                        <td style={tdStyle}><span style={{ fontSize: "0.75rem", color: "var(--wm-text-muted)" }}>{u.created_at ? new Date(u.created_at).toLocaleDateString() : "-"}</span></td>
                        <td style={{ ...tdStyle, textAlign: "center" }}>
                          <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
                            <Button variant="ghost" size="sm" title={u.is_admin ? "Remove Admin" : "Make Admin"} onClick={() => toggleAdmin(u.id, u.is_admin)} data-testid={`toggle-admin-${u.id}`}>
                              <UserCog className="w-3.5 h-3.5" style={{ color: u.is_admin ? "var(--wm-blue)" : "var(--wm-text-muted)" }} />
                            </Button>
                            <Button variant="ghost" size="sm" title="Assign Trial" onClick={() => { setTrialTarget(u); setShowTrialDialog(true); }} data-testid={`assign-trial-${u.id}`}>
                              <Clock className="w-3.5 h-3.5" style={{ color: "var(--wm-orange, #F08C00)" }} />
                            </Button>
                            <Button variant="ghost" size="sm" title="Delete User" onClick={() => deleteUser(u.id, u.email)} data-testid={`delete-user-${u.id}`}>
                              <Trash2 className="w-3.5 h-3.5" style={{ color: "var(--wm-danger)" }} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </Card>
        </div>
      )}

      {activeTab === "pricing" && (
        <div className="admin-content" data-testid="admin-pricing-tab">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
            <div>
              <h2 style={{ fontSize: "1.25rem", fontWeight: 700, color: "var(--wm-text)", margin: 0 }}>Pricing Plans</h2>
              <p style={{ fontSize: "0.8125rem", color: "var(--wm-text-muted)", margin: "4px 0 0" }}>Configure what users see on the upgrade page. Changes take effect immediately.</p>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Button onClick={() => setShowCreatePlan(true)} data-testid="add-plan-btn" className="action-btn primary-btn" style={{ fontSize: "0.8125rem" }}>
                <Plus className="w-4 h-4" /> Add Plan
              </Button>
              <Button variant="outline" onClick={resetPricing} data-testid="reset-pricing-btn" style={{ fontSize: "0.8125rem" }}>Reset to Defaults</Button>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
            {pricingPlans.map(plan => (
              <Card key={plan.plan_id} className="admin-stat-card" data-testid={`pricing-card-${plan.plan_id}`} style={{ opacity: plan.active ? 1 : 0.5, position: "relative" }}>
                {plan.plan_id === "lifetime" && <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: "var(--wm-yellow)", borderRadius: "8px 8px 0 0" }} />}
                <CardContent style={{ padding: 24 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                    <div>
                      <div style={{ fontSize: "0.6875rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--wm-blue)", marginBottom: 4 }}>{plan.plan_id}</div>
                      {editingPlan === plan.plan_id ? (
                        <Input data-testid={`edit-plan-name-${plan.plan_id}`} defaultValue={plan.name} onBlur={e => updatePlan(plan.plan_id, { name: e.target.value })} style={{ fontSize: "1.125rem", fontWeight: 700, width: 180 }} />
                      ) : (
                        <div style={{ fontSize: "1.125rem", fontWeight: 700, color: "var(--wm-text)" }}>{plan.name}</div>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: 4 }}>
                      <Button variant="ghost" size="sm" onClick={() => setEditingPlan(editingPlan === plan.plan_id ? null : plan.plan_id)} data-testid={`edit-plan-${plan.plan_id}`} style={{ fontSize: "0.75rem", color: "var(--wm-blue)" }}>
                        {editingPlan === plan.plan_id ? "Done" : "Edit"}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => deletePlan(plan.plan_id)} data-testid={`delete-plan-${plan.plan_id}`} style={{ fontSize: "0.75rem", color: "var(--wm-danger)" }}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>

                  {editingPlan === plan.plan_id ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      <div>
                        <label style={labelStyle}>Price ($)</label>
                        <Input data-testid={`edit-plan-price-${plan.plan_id}`} type="number" step="0.01" defaultValue={plan.price} onBlur={e => updatePlan(plan.plan_id, { price: parseFloat(e.target.value) })} />
                      </div>
                      <div>
                        <label style={labelStyle}>Duration (days, 0 = lifetime)</label>
                        <Input data-testid={`edit-plan-days-${plan.plan_id}`} type="number" defaultValue={plan.duration_days} onBlur={e => updatePlan(plan.plan_id, { duration_days: parseInt(e.target.value) })} />
                      </div>
                      <div>
                        <label style={labelStyle}>Description</label>
                        <Input data-testid={`edit-plan-desc-${plan.plan_id}`} defaultValue={plan.description} onBlur={e => updatePlan(plan.plan_id, { description: e.target.value })} />
                      </div>
                      <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                        <Button size="sm" variant={plan.active ? "outline" : "default"} onClick={() => updatePlan(plan.plan_id, { active: !plan.active })} data-testid={`toggle-plan-${plan.plan_id}`} style={{ fontSize: "0.75rem" }}>
                          {plan.active ? "Deactivate" : "Activate"}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div style={{ fontSize: "2rem", fontWeight: 700, color: "var(--wm-blue)", marginBottom: 4 }}>${plan.price.toFixed(2)}</div>
                      <div style={{ fontSize: "0.8125rem", color: "var(--wm-text-secondary)", marginBottom: 8 }}>{plan.description}</div>
                      <div style={{ fontSize: "0.75rem", color: "var(--wm-text-muted)" }}>
                        {plan.duration_days > 0 ? `${plan.duration_days} days` : "Unlimited"}
                        {!plan.active && <span style={{ marginLeft: 8, color: "var(--wm-danger)", fontWeight: 600 }}>INACTIVE</span>}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {activeTab === "payments" && (
        <div className="admin-content" data-testid="admin-payments-tab">
          <Card className="admin-table-card">
            <CardHeader><CardTitle>Payments ({payments.length})</CardTitle></CardHeader>
            <CardContent>
              {payments.length === 0 ? (
                <div className="admin-empty">No payments recorded yet.</div>
              ) : (
                <div className="admin-table-container">
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        <th style={thStyle}>User</th>
                        <th style={thStyle}>Plan</th>
                        <th style={thStyle}>Amount</th>
                        <th style={thStyle}>Order ID</th>
                        <th style={thStyle}>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map((p, i) => (
                        <tr key={i} style={{ borderBottom: "1px solid #f0f2f5" }}>
                          <td style={tdStyle}><span style={{ fontFamily: "monospace", fontSize: "0.8125rem" }}>{p.user_email || p.user_id}</span></td>
                          <td style={tdStyle}><span className={`plan-badge ${p.plan}`}>{p.plan}</span></td>
                          <td style={{ ...tdStyle, color: "var(--wm-success)", fontWeight: 600 }}>${p.amount?.toFixed(2)}</td>
                          <td style={tdStyle}><span style={{ fontFamily: "monospace", fontSize: "0.6875rem", color: "var(--wm-text-muted)" }}>{p.order_id?.substring(0, 16)}...</span></td>
                          <td style={{ ...tdStyle, fontSize: "0.8125rem", color: "var(--wm-text-muted)" }}>{p.completed_at ? new Date(p.completed_at).toLocaleString() : "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === "seo" && (
        <div className="admin-content" data-testid="admin-seo-tab">
          <SeoToolkit token={token} />
        </div>
      )}

      {activeTab === "tickets" && (
        <div className="admin-content" data-testid="admin-tickets-tab">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
            <div>
              <h2 style={{ fontSize: "1.25rem", fontWeight: 700, color: "var(--wm-text)", margin: 0 }}>Help Tickets</h2>
              <p style={{ fontSize: "0.8125rem", color: "var(--wm-text-muted)", margin: "4px 0 0" }}>
                {tickets.length} total ticket{tickets.length !== 1 ? "s" : ""} &middot; {tickets.filter(t => t.status === "open").length} open
              </p>
            </div>
            <Select value={ticketFilter} onValueChange={setTicketFilter}>
              <SelectTrigger data-testid="ticket-filter" style={{ width: 160 }}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tickets</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: selectedTicket ? "320px 1fr" : "1fr", gap: 16, minHeight: 400 }}>
            {/* Ticket List */}
            <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 600, overflowY: "auto" }}>
              {(ticketFilter === "all" ? tickets : tickets.filter(t => t.status === ticketFilter)).length === 0 ? (
                <div style={{ textAlign: "center", padding: 40, color: "var(--wm-text-muted)" }}>No tickets found.</div>
              ) : (
                (ticketFilter === "all" ? tickets : tickets.filter(t => t.status === ticketFilter)).map(ticket => (
                  <Card
                    key={ticket.ticket_id}
                    data-testid={`ticket-card-${ticket.ticket_id}`}
                    className="admin-stat-card"
                    style={{
                      cursor: "pointer",
                      border: selectedTicket?.ticket_id === ticket.ticket_id ? "2px solid var(--wm-blue)" : "1px solid var(--wm-border)",
                      background: selectedTicket?.ticket_id === ticket.ticket_id ? "var(--wm-blue-light)" : "var(--wm-white)",
                    }}
                    onClick={() => setSelectedTicket(ticket)}
                  >
                    <CardContent style={{ padding: 16 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                        <span style={{ fontSize: "0.6875rem", fontWeight: 700, color: "var(--wm-blue)" }}>#{ticket.ticket_id}</span>
                        <span style={{
                          fontSize: "0.625rem", fontWeight: 600, textTransform: "uppercase", padding: "2px 8px", borderRadius: 10,
                          background: ticket.status === "open" ? "#FEF3C7" : ticket.status === "in_progress" ? "#DBEAFE" : ticket.status === "resolved" ? "#D1FAE5" : "#F3F4F6",
                          color: ticket.status === "open" ? "#92400E" : ticket.status === "in_progress" ? "#1E40AF" : ticket.status === "resolved" ? "#065F46" : "#6B7280",
                        }}>{ticket.status.replace("_", " ")}</span>
                      </div>
                      <div style={{ fontSize: "0.875rem", fontWeight: 600, color: "var(--wm-text)", marginBottom: 4, lineHeight: 1.3 }}>{ticket.subject}</div>
                      <div style={{ fontSize: "0.75rem", color: "var(--wm-text-muted)" }}>
                        {ticket.email || ticket.username || "Guest"} &middot; {new Date(ticket.created_at).toLocaleDateString()}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>

            {/* Ticket Detail */}
            {selectedTicket && (
              <Card className="admin-stat-card" style={{ overflow: "hidden" }}>
                <CardContent style={{ padding: 0 }}>
                  <div style={{ padding: "20px 24px", borderBottom: "1px solid var(--wm-border)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                      <div>
                        <div style={{ fontSize: "0.6875rem", fontWeight: 700, color: "var(--wm-blue)", marginBottom: 4 }}>#{selectedTicket.ticket_id}</div>
                        <h3 style={{ fontSize: "1.1rem", fontWeight: 700, color: "var(--wm-text)", margin: 0 }}>{selectedTicket.subject}</h3>
                      </div>
                      <Select value={selectedTicket.status} onValueChange={v => updateTicketStatus(selectedTicket.ticket_id, v)}>
                        <SelectTrigger data-testid="ticket-status-select" style={{ width: 140, fontSize: "0.75rem" }}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="open">Open</SelectItem>
                          <SelectItem value="in_progress">In Progress</SelectItem>
                          <SelectItem value="resolved">Resolved</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div style={{ fontSize: "0.8125rem", color: "var(--wm-text-muted)" }}>
                      From: {selectedTicket.email || selectedTicket.username || "Guest"} &middot; {new Date(selectedTicket.created_at).toLocaleString()}
                    </div>
                  </div>

                  {/* Messages */}
                  <div style={{ padding: "16px 24px", maxHeight: 320, overflowY: "auto", display: "flex", flexDirection: "column", gap: 12 }}>
                    {selectedTicket.messages?.map((msg, i) => (
                      <div key={i} style={{
                        padding: "12px 16px",
                        borderRadius: 10,
                        maxWidth: "85%",
                        background: msg.from === "admin" ? "var(--wm-blue-light)" : "#F3F4F6",
                        alignSelf: msg.from === "admin" ? "flex-end" : "flex-start",
                        border: msg.from === "admin" ? "1px solid rgba(0,113,220,0.2)" : "1px solid var(--wm-border)",
                      }}>
                        <div style={{ fontSize: "0.6875rem", fontWeight: 600, color: msg.from === "admin" ? "var(--wm-blue)" : "var(--wm-text-muted)", marginBottom: 4, textTransform: "uppercase" }}>
                          {msg.from === "admin" ? "Admin" : "User"} &middot; {new Date(msg.timestamp).toLocaleString()}
                        </div>
                        <div style={{ fontSize: "0.875rem", color: "var(--wm-text)", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{msg.message}</div>
                      </div>
                    ))}
                  </div>

                  {/* Reply Box */}
                  <div style={{ padding: "16px 24px", borderTop: "1px solid var(--wm-border)", display: "flex", gap: 8 }}>
                    <Input
                      data-testid="ticket-reply-input"
                      value={replyMessage}
                      onChange={e => setReplyMessage(e.target.value)}
                      placeholder="Type your reply..."
                      onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); replyToTicket(selectedTicket.ticket_id); } }}
                      style={{ flex: 1 }}
                    />
                    <Button data-testid="ticket-reply-send" onClick={() => replyToTicket(selectedTicket.ticket_id)} disabled={replying} className="action-btn primary-btn" style={{ gap: 6 }}>
                      <Send className="w-4 h-4" /> {replying ? "..." : "Send"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      {activeTab === "blog" && (
        <div className="admin-content" data-testid="admin-blog-tab">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
            <div>
              <h2 style={{ fontSize: "1.25rem", fontWeight: 700, color: "var(--wm-text)", margin: 0 }}>Blog Management</h2>
              <p style={{ fontSize: "0.8125rem", color: "var(--wm-text-muted)", margin: "4px 0 0" }}>Manage your blog posts. Posts are publicly visible at /blog.</p>
            </div>
            <Link to="/blog" target="_blank" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--wm-blue)", textDecoration: "none", fontWeight: 600, fontSize: "0.875rem" }} data-testid="view-blog-link">
              <BookOpen className="w-4 h-4" /> View Blog <ExternalLink className="w-3.5 h-3.5" />
            </Link>
          </div>
          <p style={{ color: "var(--wm-text-secondary)", fontSize: "0.875rem" }}>
            Blog posts are managed via the API. Use the following endpoints:
          </p>
          <div style={{ marginTop: 16, padding: 20, background: "#F8FAFC", border: "1px solid var(--wm-border)", borderRadius: "var(--wm-radius)", fontSize: "0.8125rem", fontFamily: "monospace", color: "var(--wm-text-secondary)", lineHeight: 2 }}>
            <div><strong>GET</strong> /api/admin/blog — List all posts</div>
            <div><strong>POST</strong> /api/admin/blog — Create post</div>
            <div><strong>PUT</strong> /api/admin/blog/:slug — Update post</div>
            <div><strong>DELETE</strong> /api/admin/blog/:slug — Delete post</div>
          </div>
        </div>
      )}

      {/* Create User Dialog */}
      <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
        <DialogContent style={{ maxWidth: 480 }}>
          <DialogHeader>
            <DialogTitle style={{ display: "flex", alignItems: "center", gap: 8 }}><Plus className="w-5 h-5" style={{ color: "var(--wm-blue)" }} /> Create User</DialogTitle>
          </DialogHeader>
          <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: "8px 0" }}>
            <div>
              <label style={labelStyle}>Email / Username *</label>
              <Input data-testid="create-user-email" value={newUser.email} onChange={e => setNewUser({ ...newUser, email: e.target.value })} placeholder="user@email.com" />
            </div>
            <div>
              <label style={labelStyle}>Password *</label>
              <Input data-testid="create-user-password" type="password" value={newUser.password} onChange={e => setNewUser({ ...newUser, password: e.target.value })} placeholder="Min 6 characters" />
            </div>
            <div>
              <label style={labelStyle}>Name</label>
              <Input data-testid="create-user-name" value={newUser.name} onChange={e => setNewUser({ ...newUser, name: e.target.value })} placeholder="Display name" />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={labelStyle}>Role</label>
                <Select value={newUser.role} onValueChange={v => setNewUser({ ...newUser, role: v })}>
                  <SelectTrigger data-testid="create-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="test">Test User</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label style={labelStyle}>Plan</label>
                <Select value={newUser.plan} onValueChange={v => setNewUser({ ...newUser, plan: v })}>
                  <SelectTrigger data-testid="create-user-plan"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="monthly">Monthly ($50)</SelectItem>
                    <SelectItem value="lifetime">Lifetime ($150)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label style={labelStyle}>Trial Duration (days)</label>
              <Select value={newUser.trial_days.toString()} onValueChange={v => setNewUser({ ...newUser, trial_days: parseInt(v) })}>
                <SelectTrigger data-testid="create-user-trial"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">No trial</SelectItem>
                  <SelectItem value="1">1 day</SelectItem>
                  <SelectItem value="2">2 days</SelectItem>
                  <SelectItem value="3">3 days</SelectItem>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="14">14 days</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreateUser(false)}>Cancel</Button>
            <Button data-testid="create-user-submit" onClick={createUser} disabled={creating} className="action-btn primary-btn">
              {creating ? "Creating..." : "Create User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Trial Dialog */}
      <Dialog open={showTrialDialog} onOpenChange={setShowTrialDialog}>
        <DialogContent style={{ maxWidth: 400 }}>
          <DialogHeader>
            <DialogTitle style={{ display: "flex", alignItems: "center", gap: 8 }}><Clock className="w-5 h-5" style={{ color: "var(--wm-orange, #F08C00)" }} /> Assign Trial</DialogTitle>
          </DialogHeader>
          <div style={{ padding: "8px 0" }}>
            <p style={{ fontSize: "0.875rem", color: "var(--wm-text-secondary)", marginBottom: 16 }}>
              Assign a free trial to <strong>{trialTarget?.email}</strong>. This gives full access for the selected duration.
            </p>
            <label style={labelStyle}>Trial Duration</label>
            <Select value={trialDays} onValueChange={setTrialDays}>
              <SelectTrigger data-testid="trial-days-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 day</SelectItem>
                <SelectItem value="2">2 days</SelectItem>
                <SelectItem value="3">3 days</SelectItem>
                <SelectItem value="7">7 days</SelectItem>
                <SelectItem value="14">14 days</SelectItem>
                <SelectItem value="30">30 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowTrialDialog(false)}>Cancel</Button>
            <Button data-testid="assign-trial-submit" onClick={assignTrial} className="action-btn primary-btn">Assign Trial</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Plan Dialog */}
      <Dialog open={showCreatePlan} onOpenChange={setShowCreatePlan}>
        <DialogContent style={{ maxWidth: 480 }}>
          <DialogHeader>
            <DialogTitle style={{ display: "flex", alignItems: "center", gap: 8 }}><Tag className="w-5 h-5" style={{ color: "var(--wm-blue)" }} /> Create Pricing Plan</DialogTitle>
          </DialogHeader>
          <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: "8px 0" }}>
            <div>
              <label style={labelStyle}>Plan ID * (lowercase, no spaces)</label>
              <Input data-testid="create-plan-id" value={newPlan.plan_id} onChange={e => setNewPlan({ ...newPlan, plan_id: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })} placeholder="e.g. quarterly" />
            </div>
            <div>
              <label style={labelStyle}>Plan Name *</label>
              <Input data-testid="create-plan-name" value={newPlan.name} onChange={e => setNewPlan({ ...newPlan, name: e.target.value })} placeholder="e.g. 3 Months" />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={labelStyle}>Price ($) *</label>
                <Input data-testid="create-plan-price" type="number" step="0.01" value={newPlan.price} onChange={e => setNewPlan({ ...newPlan, price: parseFloat(e.target.value) || 0 })} />
              </div>
              <div>
                <label style={labelStyle}>Duration (days, 0 = lifetime)</label>
                <Input data-testid="create-plan-days" type="number" value={newPlan.duration_days} onChange={e => setNewPlan({ ...newPlan, duration_days: parseInt(e.target.value) || 0 })} />
              </div>
            </div>
            <div>
              <label style={labelStyle}>Description</label>
              <Input data-testid="create-plan-desc" value={newPlan.description} onChange={e => setNewPlan({ ...newPlan, description: e.target.value })} placeholder="e.g. 90 days of full access" />
            </div>
            <div>
              <label style={labelStyle}>Sort Order</label>
              <Input data-testid="create-plan-sort" type="number" value={newPlan.sort_order} onChange={e => setNewPlan({ ...newPlan, sort_order: parseInt(e.target.value) || 99 })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreatePlan(false)}>Cancel</Button>
            <Button data-testid="create-plan-submit" onClick={createPlan} disabled={creatingPlan} className="action-btn primary-btn">
              {creatingPlan ? "Creating..." : "Create Plan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const thStyle = {
  background: "var(--wm-blue-light)",
  padding: "10px 12px",
  fontSize: "0.75rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "var(--wm-blue-dark)",
  textAlign: "left",
  borderBottom: "2px solid var(--wm-blue)",
  whiteSpace: "nowrap",
};

const tdStyle = {
  padding: "10px 12px",
  color: "var(--wm-text)",
  verticalAlign: "middle",
};

const labelStyle = {
  display: "block",
  fontSize: "0.8125rem",
  fontWeight: 500,
  color: "var(--wm-text-secondary)",
  marginBottom: 4,
};
